/**
 * Copyright (c) 2018-2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { CoarseHierarchy } from '../../../mol-model/structure/model/properties/coarse';
import { getCoarseKeys } from '../../../mol-model/structure/model/properties/utils/coarse-keys';
import { UUID } from '../../../mol-util';
import { Segmentation, Interval } from '../../../mol-data/int';
import { Mat3, Tensor } from '../../../mol-math/linear-algebra';
import { getCoarseRanges } from '../../../mol-model/structure/model/properties/utils/coarse-ranges';
import { BasicSchema } from './schema';
export var EmptyCoarse = { hierarchy: CoarseHierarchy.Empty, conformation: void 0 };
export function getCoarse(data, properties) {
    var ihm_sphere_obj_site = data.ihm_sphere_obj_site, ihm_gaussian_obj_site = data.ihm_gaussian_obj_site;
    if (ihm_sphere_obj_site._rowCount === 0 && ihm_gaussian_obj_site._rowCount === 0)
        return EmptyCoarse;
    var sphereData = getData(ihm_sphere_obj_site);
    var sphereConformation = getSphereConformation(ihm_sphere_obj_site);
    var sphereKeys = getCoarseKeys(sphereData, data.entities);
    var sphereRanges = getCoarseRanges(sphereData, properties.chemicalComponentMap);
    var gaussianData = getData(ihm_gaussian_obj_site);
    var gaussianConformation = getGaussianConformation(ihm_gaussian_obj_site);
    var gaussianKeys = getCoarseKeys(gaussianData, data.entities);
    var gaussianRanges = getCoarseRanges(gaussianData, properties.chemicalComponentMap);
    return {
        hierarchy: {
            isDefined: true,
            spheres: __assign(__assign(__assign({}, sphereData), sphereKeys), sphereRanges),
            gaussians: __assign(__assign(__assign({}, gaussianData), gaussianKeys), gaussianRanges),
        },
        conformation: {
            id: UUID.create22(),
            spheres: sphereConformation,
            gaussians: gaussianConformation
        }
    };
}
function getSphereConformation(data) {
    return {
        x: data.Cartn_x.toArray({ array: Float32Array }),
        y: data.Cartn_y.toArray({ array: Float32Array }),
        z: data.Cartn_z.toArray({ array: Float32Array }),
        radius: data.object_radius.toArray({ array: Float32Array }),
        rmsf: data.rmsf.toArray({ array: Float32Array })
    };
}
function getGaussianConformation(data) {
    var matrix_space = BasicSchema.ihm_gaussian_obj_site.covariance_matrix.space;
    var covariance_matrix = [];
    var cm = data.covariance_matrix;
    for (var i = 0, _i = cm.rowCount; i < _i; i++) {
        covariance_matrix[i] = Tensor.toMat3(Mat3(), matrix_space, cm.value(i));
    }
    return {
        x: data.mean_Cartn_x.toArray({ array: Float32Array }),
        y: data.mean_Cartn_y.toArray({ array: Float32Array }),
        z: data.mean_Cartn_z.toArray({ array: Float32Array }),
        weight: data.weight.toArray({ array: Float32Array }),
        covariance_matrix: covariance_matrix
    };
}
function getSegments(asym_id, seq_id_begin, seq_id_end) {
    var chainOffsets = [0];
    for (var i = 1, _i = asym_id.rowCount; i < _i; i++) {
        var newChain = !asym_id.areValuesEqual(i - 1, i);
        if (newChain)
            chainOffsets[chainOffsets.length] = i;
    }
    return {
        chainElementSegments: Segmentation.ofOffsets(chainOffsets, Interval.ofBounds(0, asym_id.rowCount))
    };
}
function getData(data) {
    var entity_id = data.entity_id, seq_id_begin = data.seq_id_begin, seq_id_end = data.seq_id_end, asym_id = data.asym_id;
    return __assign({ count: entity_id.rowCount, entity_id: entity_id, asym_id: asym_id, seq_id_begin: seq_id_begin, seq_id_end: seq_id_end }, getSegments(asym_id, seq_id_begin, seq_id_end));
}
//# sourceMappingURL=coarse.js.map